import { getCurrentInstance, nextTick, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { isOfficeAddinHost } from '@/office/utils';
import { useAuthStore } from '@/stores/auth';
import { useChangelogStore } from '@/stores/changelogStore';
import { useUserAttentionStore } from '@/stores/userAttentionStore';
import { useGaiaStore } from '@/stores/gaiaStore';
import { useOrganizationStore } from '@/stores/organizationStore';
import { usePromptCollectionsStore } from '@/stores/promptCollectionsStore';
import { usePromptLibraryStore } from '@/stores/prompt_library';
import { useSessionGuardStore } from '@/stores/sessionGuardStore';
import { useTourStore } from '@/stores/tourStore';
import { logger } from '@/utils/logger';

// Module scope: App.vue swaps layouts with <component :is>, which unmounts and
// remounts the authenticated layout on every layout switch (e.g. default →
// external → default). Keying on the signed-in user's uid makes those remounts
// no-ops while still re-bootstrapping after logout / login as another user.
let bootstrappedUid: string | null = null;

/**
 * One-time per-session bootstrap for authenticated surfaces: prompt library,
 * GAIA integration token, Hotjar identification, and the first-sign-up
 * collection import. Called from the authenticated layouts (DefaultLayout,
 * OfficeLayout) — public layouts (external, login) must never run this.
 *
 * `checkChangelogs` additionally runs the boot-time "new changelogs since
 * last seen" check (auto-opens the changelog modal). Only the main app opts
 * in — the modal is a poor fit for the Office add-in's narrow taskpane.
 */
export function useAuthenticatedBootstrap(options?: { checkChangelogs?: boolean }): void {
  const authStore = useAuthStore();
  const promptLibraryStore = usePromptLibraryStore();
  const gaiaStore = useGaiaStore();
  const changelogStore = useChangelogStore();
  const userAttentionStore = useUserAttentionStore();
  const collectionsStore = usePromptCollectionsStore();
  const organizationStore = useOrganizationStore();
  const route = useRoute();
  const router = useRouter();

  // Captured synchronously: getCurrentInstance() returns null inside async
  // watch callbacks.
  const globalProperties = getCurrentInstance()?.appContext.config.globalProperties;

  // Function to import prompt library collections based on query parameters
  async function importLibraryCollections(): Promise<void> {
    try {
      // Ensure the prompt library is initialized first
      if (!promptLibraryStore.initialized) {
        await promptLibraryStore.initialize();
      }
      // Fetch existing collections to check against
      await collectionsStore.fetchCollections();

      // Get contract types to import from query parameter
      const contractTypes = String(route.query['contract-types']).split(',');
      // Find and import each contract type from the meta categories
      for (const metaCategory of promptLibraryStore.metaCategories) {
        if (contractTypes.includes(metaCategory.name)) {
          for (const category of metaCategory.categories) {
            // Check if a collection with this name already exists
            const collectionName = `${metaCategory.name} - ${category.name}`;
            const existingCollection = collectionsStore.collections.find(
              c => c.name === collectionName
            );

            if (!existingCollection) {
              // Prepare prompts for import
              const prompts = category.prompts.map(prompt => {
                // Create the prompt object with proper typing
                return {
                  name: prompt.name,
                  prompt: prompt.prompt,
                  response_type: prompt.response_type,
                  // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty string must normalise to null
                  evaluation_prompt: prompt.evaluation_prompt || null,
                  // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty string must normalise to null
                  evaluation_response_type: prompt.evaluation_response_type || null,
                  // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty string must normalise to null
                  suggestion_prompt: prompt.suggestion_prompt || null,
                };
              });

              // Create new collection
              const newCollection = await collectionsStore.createCollection(collectionName);

              // Add prompts to the collection
              if (newCollection) {
                for (const prompt of prompts) {
                  await collectionsStore.addPrompt(newCollection.uid, prompt);
                }
              }
            }
          }
        }
      }
      // Remove query parameters
      const newQuery = { ...route.query };
      delete newQuery['first-sign-up'];
      delete newQuery['contract-types'];

      // Replace the current route with the new query parameters
      await router.replace({ query: newQuery });
    } catch (error) {
      logger.error('Error importing collections:', error);
      // Don't show error to user - silent failure
    }
  }

  watch(
    () => authStore.isReady && authStore.isAuthenticated,
    async isAuthenticated => {
      if (!isAuthenticated) {
        bootstrappedUid = null;
        // Attention badges are per-user: never let the next sign-in inherit them.
        userAttentionStore.reset();
        return;
      }

      const backendUser = authStore.backendUser;
      if (!backendUser || backendUser.uid === bootstrappedUid) {
        return;
      }
      bootstrappedUid = backendUser.uid;

      if (globalProperties?.$hj) {
        globalProperties.$hj('identify', backendUser.uid, {
          email: backendUser.email,
          name: backendUser.full_name,
        });
      }

      if (!promptLibraryStore.initialized) {
        await promptLibraryStore.initialize();
      }
      // Initialize GAIA token for integrations
      await gaiaStore.fetchCurrentToken();

      // The active organization gates feature checks and org-scoped views all
      // over the app, so it is loaded once per session here. An org-less user
      // has nothing to load — the endpoint would only fail.
      if (authStore.hasActiveOrganisation) {
        try {
          await organizationStore.fetchActiveOrganization();
        } catch (error) {
          logger.error('Error initializing organization data:', error);
        }
      }

      if (options?.checkChangelogs) {
        // Fire-and-forget: a slow/failing changelog request must not delay boot.
        void changelogStore.checkForNewChangelogs();
      }

      // What is waiting on the user (chat threads, signing turns) — the home
      // preview and the chat badges. Fire-and-forget; it also starts the live event listener.
      userAttentionStore.initialize();

      // Follows organisation and access changes made outside this tab.
      useSessionGuardStore().initialize();

      if (!isOfficeAddinHost()) {
        // Fire-and-forget: loads which tours are done so they can auto-start.
        // Tours are a poor fit for the Office add-in's narrow taskpane.
        void useTourStore().initialize();
      }

      if ('first-sign-up' in route.query && 'contract-types' in route.query) {
        void nextTick(async () => {
          await importLibraryCollections();
        });
      }
    },
    { immediate: true }
  );
}
